import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData, deleteData } from "../../../utility/Utility";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

function CommissionRuleList() {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const rowsPerPage = 10;

  // Fetch rules
  const fetchRules = async (page = 0) => {
    setLoading(true);
    try {
      const response = await postData("api/commission-rules/list", {
        page: page + 1,
        limit: rowsPerPage,
      });

      if (response?.success) {
        setRules(response?.data || []);
        setTotalItems(response?.pagination?.totalItems || response?.data?.length || 0);
        setPageCount(response?.pagination?.totalPages || 1);
      } else {
        toast.error(response?.message || "Failed to fetch commission rules");
      }
    } catch (error) {
      console.error("Fetch rules error:", error);
      toast.error("Error fetching commission rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules(currentPage);
  }, [currentPage]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleDelete = async (ruleId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await deleteData("api/commission-rules/delete", {
            ruleId,
          });
          if (response?.success) {
            toast.success(response?.message || "Deleted successfully");
            fetchRules(currentPage);
          } else {
            toast.error(response?.message || "Failed to delete");
          }
        } catch (error) {
          console.error("Delete error:", error);
          toast.error("Error deleting commission rule");
        }
      }
    });
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <CustomLoader loading={loading} />

      <div className="content">
        <div className="container-fluid">
          {/* Header Section */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <h4 className="fs-18 fw-semibold m-0">Commission Rules</h4>
            <button
              className="btn btn-primary"
              onClick={() => navigate("/add-commission-rule")}
            >
              <i className="mdi mdi-plus me-1"></i>
              Create Rule
            </button>
          </div>

          {/* Table */}
          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Category</th>
                            <th>SubCategory</th>
                            <th>Policy Type</th>
                            {/* <th>Slabs</th> */}
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rules.length > 0 ? (
                            rules.map((rule, index) => (
                              <tr key={rule._id}>
                                <td>{currentPage * rowsPerPage + index + 1}</td>
                                <td>{rule?.category?.title || "N/A"}</td>
                                <td>{rule?.subcategory?.title || "N/A"}</td>
                                <td className="text-capitalize">{rule.policyType}</td>
                                {/* <td>
                                  {rule.slabRules?.map((slab, i) => (
                                    <span
                                      key={i}
                                      className="badge bg-light text-dark me-1 mb-1"
                                      title={`Min: ${slab.minAmount}, Max: ${slab.maxAmount}, Rate: ${slab.rate}%`}
                                    >
                                      {slab.minAmount}-{slab.maxAmount}: {slab.rate}%
                                    </span>
                                  ))}
                                </td> */}
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      title="Edit Rule"
                                      onClick={() =>
                                        navigate(`/add-commission-rule`, {
                                          state: { ruleData:rule },
                                        })
                                      }
                                    >
                                      <i className="mdi mdi-pencil"></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      title="Delete Rule"
                                      onClick={() => handleDelete(rule._id)}
                                    >
                                      <i className="mdi mdi-delete"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center py-4">
                                No rules found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {/* Pagination */}
                      {pageCount > 1 && (
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            Showing {currentPage * rowsPerPage + 1} to{" "}
                            {Math.min((currentPage + 1) * rowsPerPage, totalItems)} of{" "}
                            {totalItems} entries
                          </div>
                          <ReactPaginate
                            previousLabel={<i className="mdi mdi-chevron-left"></i>}
                            nextLabel={<i className="mdi mdi-chevron-right"></i>}
                            breakLabel="..."
                            pageCount={pageCount}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            onPageChange={handlePageChange}
                            containerClassName="pagination pagination-rounded justify-content-end mt-3"
                            pageClassName="page-item"
                            pageLinkClassName="page-link"
                            previousClassName="page-item"
                            previousLinkClassName="page-link"
                            nextClassName="page-item"
                            nextLinkClassName="page-link"
                            activeClassName="active"
                            forcePage={currentPage}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> 
    </div>
  );
}

export default CommissionRuleList;
